import { useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import {
  addTranslationData,
  useTranslation,
} from "@mywheels/common/lib/translation";
import { useToggle } from "@mywheels/common/util";
import { useMoveCountdown } from "@mywheels/common/lib/move_campaign";
import {
  BooleanParam,
  useQueryParam,
} from "@mywheels/common/util/useQueryParams";

import { ToolbarSpacer } from "web/layout/Toolbar";
import { ModalPortal, useScrollToTop } from "web/ui";
import { CheckIcon } from "web/ui/icons/checkboxes";
import { ExternalLinkIcon } from "web/ui/icons/ExternalLinkIcon";
import { MovePageTermsPaneModal } from "./MovePageTermsPaneModal";
import { VolumeMuteIcon } from "web/ui/icons/VolumeMuteIcon";
import { VolumeUpIcon } from "web/ui/icons/VolumeUpIcon";
import { PauseIcon } from "web/ui/icons/PauseIcon";
import { PlayIcon } from "web/ui/icons/PlayIcon";
import {
  DownloadFromAppStore,
  DownloadFromGooglePlay,
} from "web/ui/icons/store";

import { MoveWall } from "./MoveWall";
import { CampaignEndedBanner } from "./CampaignEndedBanner";

addTranslationData({
  en: {
    "Bekijk de commercial": "Watch the commercial",
    "Doe 'The Move'": "Do 'The Move'",
    "Bekijk de moves op Instagram": "View the moves on Instagram",
    "Win een jaar lang": "Win one year's of",
    "gratis autorijden": "free driving",
    "Deel jouw beste move in een Instagram story of post":
      "Share your best Move in an Instagram post or Story",
    "Gebruik de hashtag #deelmee en tag en volg":
      "Use the hashtag #deelmee and tag and follow",
    "Wij delen meer dan": "We're handing out",
    "60 prijzen uit!": "more than 60 prizes!",
    "Een jaar lang gratis autorijden met MyWheels":
      "A year of free driving with MyWheels",
    "MyWheels goodiebag t.w.v. €50,-": "MyWheels goody bag worth €50",
    "MyWheels rijtegoed van €35,-": "MyWheels journey balance of €35",
    "Winnen?": "How to win?",
    "Je maakt na het delen van jouw video op Instagram kans op héél veel prijzen! Alle moves worden opgedeeld in drie categorieën:":
      "Share your video on Instagram and have a chance at winning lots of prizes! All Moves will be divided into three categories:",
    "→ De meest originele move": "→ The most original Move",
    "→ De grappigste move": "→ The funniest Move",
    "→ De beste move met een auto": "→ The best Move with a car",
    "Binnen elke categorie delen we meer dan 20 prijzen uit.":
      "We're handing out more than 20 prizes within each category.",
  },
});

export default function MovePage() {
  const { t } = useTranslation();

  useScrollToTop();

  const [muted, toggleMuted] = useToggle(true);

  const [ref, ctaInView] = useInView();

  return (
    <>
      <div className="overflow-hidden" style={{ marginBottom: "-7rem" }}>
        <div className="bg-blue" ref={ref}>
          <ToolbarSpacer />
          <CampaignEndedBanner />

          <div className="px-3 mx-auto md:max-w-[840px] pt-5 md:pt-10">
            <div className="row">
              <div className="col-12 md:col-6">
                <div className="pb-7">
                  <HeroTextBlock />
                </div>
              </div>
              <div className="col-12 md:col-6 relative z-10">
                <div className="md:ml-6 max-h-[266px] overflow-visible">
                  <div className="relative mx-auto mb-8 md:w-full w-[150px] rounded-md overflow-hidden">
                    <div
                      className="pt-[176%] relative bg-cover bg-center"
                      style={{
                        backgroundImage: `url("https://media.graphcms.com/OT0aPlAQCK2KiL7xyItg")`,
                      }}
                    >
                      <video
                        playsInline
                        autoPlay
                        muted={muted}
                        loop
                        className="absolute inset-0"
                      >
                        <source
                          src="https://media.graphcms.com/XE8jeB2qQI67BYkl20r5"
                          type="video/mp4"
                        />
                      </video>
                      <div
                        className="group absolute inset-0 flex justify-center items-center text-white cursor-pointer"
                        onClick={toggleMuted}
                      >
                        <div className="w-[52px] h-[52px] flex justify-center items-center rounded-full bg-black bg-opacity-10 transition-transform group-active:scale-95 group-hover:scale-110">
                          {muted ? (
                            <VolumeMuteIcon size={28} />
                          ) : (
                            <VolumeUpIcon size={28} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-10">
                <div className="pb-10">
                  <InstaBtn />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-3 mx-auto md:max-w-[840px] pt-9 md:pt-12 mb-8 md:mb-13">
          <div className="row">
            <div className="col-12 md:col-6">
              <div className="pb-7">
                <ContentTextBlock />
              </div>
            </div>
          </div>
        </div>

        <div className="px-3 mx-auto md:max-w-[840px]">
          <Commercial />
          <div className="pt-6 pb-10">
            <div className="row">
              <div className="col-12 md:col-6">
                <h2 className="text-2xl md:text-3xl text-center md:text-left">
                  {t("Download de app.")}
                </h2>
              </div>
              <div className="col-12 md:col-6 flex justify-center md:justify-end items-center mt-4 md:mt-0">
                <div className="mr-2">
                  <a
                    href="https://hdtm.adj.st/mywheels://mywheels.nl/registreren?adj_t=dykfd5p&adj_campaign=IOS&adj_fallback=https%3A%2F%2Fmywheels.nl&adj_redirect=https%3A%2F%2Fapps.apple.com%2Fnl%2Fapp%2Fdeelauto%2Fid520472426"
                    target="_blank"
                    rel="noopener noreferrer"
                    id="IOS"
                  >
                    <DownloadFromAppStore
                      style={{ height: 40, color: "#222" }}
                      fill="white"
                      className="ml-1 mb-1"
                    />
                  </a>
                </div>
                <div>
                  <a
                    href="https://hdtm.adj.st/mywheels://mywheels.nl/registreren?adj_t=dykfd5p&adj_campaign=ANDROID&adj_fallback=https%3A%2F%2Fmywheels.nl&adj_redirect=https%3A%2F%2Fapps.apple.com%2Fnl%2Fapp%2Fdeelauto%2Fid520472426"
                    target="_blank"
                    rel="noopener noreferrer"
                    id="android"
                  >
                    <DownloadFromGooglePlay
                      style={{ height: 40, color: "#222" }}
                      fill="white"
                      className="ml-1 mb-1"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-13 px-3 mx-auto md:max-w-[840px]">
          <h2 className="text-3xl md:text-4xl text-center">
            {t("Doe 'The Move'")}
          </h2>
          <div className="mb-6 font-displaytext text-3xl md:text-4xl text-center text-black opacity-40">
            #deelmee
          </div>

          <div className="mb-13 text-center font-displaytext md:max-w-[580px] mx-auto">
            <p className="mb-2">
              {t(
                "Je maakt na het delen van jouw video op Instagram kans op héél veel prijzen! Alle moves worden opgedeeld in drie categorieën:"
              )}
            </p>
            <p className="mb-2">
              {t("→ De meest originele move")}
              <br />
              {t("→ De grappigste move")}
              <br />
              {t("→ De beste move met een auto")}
            </p>
            <p>
              {t("Binnen elke categorie delen we meer dan 20 prijzen uit.")}{" "}
              <span className="font-display">{t("Get moving!")}</span>
            </p>
          </div>

          <MoveWall />
        </div>
      </div>

      {!ctaInView && (
        <ModalPortal>
          <InstaStickyBtn />
        </ModalPortal>
      )}
    </>
  );
}

function Commercial() {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);

  const isPlaying = useRef(false);
  const [playing, setPlaying] = useState(0); // initial: 0, playing: 1, paused: 2

  return (
    <div className="select-none relative pt-[56%] bg-gray200 rounded-md overflow-hidden">
      <div className="absolute inset-0">
        <video
          playsInline
          ref={videoRef}
          autoPlay={false}
          className="absolute inset-0"
          poster="https://media.graphcms.com/vxxRjuQJTqvOXSdDQfru"
        >
          <source
            src="https://media.graphcms.com/w1SnqohWSoemuQmn9GGn"
            type="video/mp4"
          />
        </video>
      </div>

      <div
        className="group absolute inset-0 flex flex-col justify-center items-center text-white cursor-pointer"
        onClick={() => {
          if (isPlaying.current) {
            isPlaying.current = false;
            setPlaying(2);
            videoRef.current?.pause();
          } else {
            isPlaying.current = true;
            setPlaying(1);
            videoRef.current?.play();
          }
        }}
      >
        <div className="w-[52px] h-[52px] flex justify-center items-center rounded-full bg-black bg-opacity-10 transition-transform group-active:scale-95 group-hover:scale-110">
          {playing === 1 ? <PauseIcon size={28} /> : <PlayIcon size={28} />}
        </div>
        {playing === 0 && (
          <div className="relative self-stretch">
            <div className="absolute left-0 right-0 text-center pt-2 font-display text-lg">
              {t("Bekijk de commercial")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// https://graph.facebook.com/v11.0/instagram_oembed?url=https://www.instagram.com/p/BzlRvWlIAlV/&access_token=332330023550048|0hW2a8HDlASgvcpOYrrYpqchtOY

function InstaBtn() {
  const { t } = useTranslation();

  return (
    <div className="flex justify-center md:justify-start">
      <a
        id="Insta Fold"
        href="https://www.instagram.com/mywheelsnl/"
        target="_blank"
        rel="noreferrer"
        className="block transition-transform active:scale-95 focus:underline hover:underline bg-white text-center text-blue font-display py-2 px-5 rounded shadow whitespace-nowrap focus:outline-none select-none"
      >
        <ExternalLinkIcon className="nudge-up mr-1 -ml-1" />
        {t("Bekijk de moves op Instagram")}
      </a>
    </div>
  );
}

function InstaStickyBtn() {
  const { t } = useTranslation();

  return (
    <div className="fixed bottom-[24px] left-[50%]">
      <div className="translate-x-[-50%]">
        <a
          id="Insta Sticky"
          href="https://www.instagram.com/mywheelsnl/"
          target="_blank"
          rel="noreferrer"
          className="animate-success block transition-transform active:scale-95 focus:underline hover:underline bg-gray700 text-center text-white font-display py-2 px-5 rounded shadow whitespace-nowrap focus:outline-none select-none"
        >
          <ExternalLinkIcon className="nudge-up mr-1 -ml-1" />
          {t("Bekijk de moves op Instagram")}
        </a>
      </div>
    </div>
  );
}

function HeroTextBlock() {
  const { t } = useTranslation();

  const countdown = useMoveCountdown();

  return (
    <div className="font-display text-white mx-auto max-w-[300px] md:max-w-none md:mx-0">
      <h1 className="text-3xl md:text-4xl text-normal">
        {t("Win een jaar lang")}
        <br />
        {t("gratis autorijden")}
      </h1>
      <div className="mt-1 opacity-60 font-displaytext mb-4">{countdown}</div>

      <p className="mb-2 font-display">{t("Winnen?")}</p>
      <ul className="text-white font-displaytext pl-[32px]">
        <li className="mb-2 leading-[20px]">
          <CheckIcon size={24} className="ml-[-32px] mr-[8px] -my-1" />
          {t("Deel jouw beste move in een Instagram story of post")}
        </li>
        <li className="mb-2 leading-[20px]">
          <CheckIcon size={24} className="ml-[-32px] mr-[8px] -my-1" />
          {t("Gebruik de hashtag #deelmee en tag en volg")}{" "}
          <a
            className="ml-1 font-display underline"
            href="https://www.instagram.com/mywheelsnl/"
            target="_blank"
            rel="noreferrer"
          >
            @MyWheelsNL
          </a>
        </li>
      </ul>
    </div>
  );
}

function ContentTextBlock() {
  const { t } = useTranslation();

  const [showTerms, setShowTerms] = useQueryParam("voorwaarden", BooleanParam);

  return (
    <>
      {showTerms && (
        <MovePageTermsPaneModal onDismiss={() => setShowTerms(false)} />
      )}

      <div className="font-display mx-auto max-w-[300px] md:max-w-none md:mx-0">
        <h2 className="text-3xl md:text-4xl text-normal mb-6">
          {t("Wij delen meer dan")}
          <br />
          {t("60 prijzen uit!")}
        </h2>

        <div className="mb-6">
          <PrizeRow
            num={3}
            text={t("Een jaar lang gratis autorijden met MyWheels")}
          />
          <PrizeRow num={15} text={t("MyWheels goodiebag t.w.v. €50,-")} />
          <PrizeRow num={45} text={t("MyWheels rijtegoed van €35,-")} />
        </div>

        <button
          onClick={() => setShowTerms(true)}
          className="text-sm text-muted underline focus:text-gray700 focus:outline-none"
        >
          {t("Bekijk de voorwaarden")}
        </button>
      </div>
    </>
  );
}

function PrizeRow({ num = 0, text = "" }) {
  return (
    <div className="mb-3 flex flex-row items-center">
      <div className="text-white bg-yellow rounded-full w-[32px] h-[32px] flex justify-center items-center font-displaytext mb-0 mr-4">
        {num}x
      </div>
      <div className="text-left">{text}</div>
    </div>
  );
}
